import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  HomeIcon,
  DocumentIcon,
  FolderIcon,
  QuestionMarkCircleIcon,
  Cog6ToothIcon,
  BuildingOfficeIcon,
  UsersIcon,
  ShieldCheckIcon,
  UserGroupIcon,
  BellIcon,
  DocumentTextIcon,
  ComputerDesktopIcon,
  MagnifyingGlassIcon,
  RectangleStackIcon,
  KeyIcon,
  LanguageIcon,
  LockClosedIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
  EnvelopeIcon,
  ClipboardDocumentListIcon,
} from '@heroicons/react/24/outline';
import GlobalSearchModal from '../GlobalSearchModal';
import apiService from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { useNotifications } from '../../contexts/NotificationsContext';
import { useTranslation } from 'react-i18next';
import ModeSelector, { ViewMode } from './ModeSelector';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();
  const { devices, isLoading: userLoading } = useUser();
  const { isOnboardingStepHighlight, isOnboardingModeSwitchStep, currentStep } = useOnboarding();
  const hasDesktopDevice = (devices ?? []).some((d: { type?: string }) => d.type === 'desktop');
  const showDownloadsIndicator = !userLoading && !hasDesktopDevice;
  const isOnboardingOpen = currentStep !== null;
  const { t } = useTranslation('sidebar');
  const { preferences } = useUserPreferences();
  const { unreadCount } = useNotifications();

  const [isManuallyCollapsed, setIsManuallyCollapsed] = useState<boolean | null>(() => {
    const saved = localStorage.getItem('sidebar_collapsed');
    return saved ? JSON.parse(saved) : null;
  });
  const [isDragging, setIsDragging] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const dragStartX = useRef<number>(0);
  const sidebarRef = useRef<HTMLDivElement>(null);
  // View mode: 'user' | 'admin' | 'sysadmin'
  // For sysadmins: can switch between all 3 modes
  // For company admins: can switch between 'user' and 'admin' modes
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('view_mode');
    if (saved) {
      const mode = saved as ViewMode;
      // Validate mode based on user role
      if (user?.role === 'sysadmin') {
        return mode; // Sysadmins can use any mode
      } else if (user?.role === 'admin') {
        return mode === 'sysadmin' ? 'admin' : mode; // Admins can't use sysadmin mode
      }
      return 'user'; // Regular users always in user mode
    }
    // Default: sysadmin mode for sysadmins (all assets), admin mode for company admins, user for rest
    if (user?.role === 'sysadmin') return 'sysadmin';
    if (user?.role === 'admin') return 'admin';
    return 'user';
  });
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  // Check if web notifications are enabled (default to true if preferences not loaded yet)
  const webNotificationsEnabled = preferences?.web_notifications !== false;

  // Check if user is admin (company admin or sysadmin)
  const isAdmin = user && (user.role === 'admin' || user.role === 'sysadmin');
  const isSysadmin = user?.role === 'sysadmin';
  const isTranslator = !!(user && user.role && user.role.startsWith('translator_'));
  const isCompanyAuditor = user?.role === 'company_auditor';
  const isSystemAuditor = user?.role === 'system_auditor';
  const isAuditor = isCompanyAuditor || isSystemAuditor;

  // Company admins: show Organisations in sidebar only if their company manages at least one other org
  const { data: companiesForAdmin } = useQuery({
    queryKey: ['companies'],
    queryFn: () => apiService.getCompanies(),
    enabled: !!user && user.role === 'admin',
  });

  // Resources hidden for now; re-enable query when Resources nav is back
  // const { data: companyResources } = useQuery({
  //   queryKey: ['company-resources', user?.company_id],
  //   queryFn: () => apiService.getCompanyResources(user!.company_id!),
  //   enabled: !!user?.company_id && user.role === 'admin',
  // });
  const hasManagedCompanies = (companiesForAdmin?.length ?? 0) > 0;
  
  // Determine effective mode (sysadmins can switch, others are restricted)
  const effectiveMode = isSysadmin ? viewMode : (user?.role === 'admin' ? (viewMode === 'sysadmin' ? 'admin' : viewMode) : 'user');

  // Reader mode: reduced sidebar for regular users only. Admin/sysadmin in reader mode (e.g. suspended company) still see full admin nav (Users, Policies, etc.).
  const isReader = user?.mode === 'reader' && !isAdmin;

  // Role-based navigation
  const getNavigation = () => {
    if (!user) return [];

    // Auditors: read-only, only see Logs (company-scoped or system-scoped). Check before reader so auditors with mode reader still see Logs.
    if (isCompanyAuditor) {
      return [{ name: t('navigation.logs'), href: '/logs', icon: ClipboardDocumentListIcon }];
    }
    if (isSystemAuditor) {
      return [{ name: t('navigation.logs'), href: '/administration/logs', icon: ClipboardDocumentListIcon }];
    }

    // Reader mode: only Assets and Notifications (no Dashboard, Folders, Policies, Access logs, etc.)
    if (isReader) {
      const readerNav = [
        { name: t('navigation.assets'), href: '/assets', icon: DocumentIcon },
      ];
      if (webNotificationsEnabled) {
        readerNav.push({ name: t('navigation.notifications'), href: '/notifications', icon: BellIcon });
      }
      return readerNav;
    }

    // Translators see only Dashboard, Notifications, and Translations
    if (isTranslator) {
      const translatorNav = [
        { name: t('navigation.dashboard'), href: '/', icon: HomeIcon },
      ];
      
      // Add notifications link only if web notifications are enabled
      if (webNotificationsEnabled) {
        translatorNav.push({ name: t('navigation.notifications'), href: '/notifications', icon: BellIcon });
      }
      
      // Add translations link
      translatorNav.push({ name: t('navigation.translations'), href: '/translations', icon: LanguageIcon });
      
      return translatorNav;
    }

    const baseNavigation = [
      { name: t('navigation.dashboard'), href: '/', icon: HomeIcon },
    ];

    // Build navigation items
    const navigationItems = [
      { name: t('navigation.assets'), href: '/assets', icon: DocumentIcon },
      { name: t('navigation.folders'), href: '/folders', icon: FolderIcon },
      { name: t('navigation.policies'), href: '/policies', icon: ShieldCheckIcon },
    ];

    // Add notifications link only if web notifications are enabled
    if (webNotificationsEnabled) {
      navigationItems.push({ name: t('navigation.notifications'), href: '/notifications', icon: BellIcon });
    }

    // Role-specific items (invitations only for active users – suspended org users are inactive)
    if (user.role === 'user') {
      const items = [
        ...baseNavigation,
        ...navigationItems,
      ];
      if (user.is_active !== false) {
        items.push({ name: t('navigation.invitations'), href: '/invitations', icon: EnvelopeIcon });
      }
      items.push(
        { name: t('navigation.logs'), href: '/logs', icon: ClipboardDocumentListIcon },
        { name: t('navigation.devices'), href: '/devices', icon: ComputerDesktopIcon },
        { name: t('navigation.contacts'), href: '/contacts', icon: UserGroupIcon }
      );
      return items;
    }

    if (user.role === 'admin' || user.role === 'sysadmin') {
      // In user mode, admins/sysadmins see the same personal links as regular users.
      const items = [
        ...baseNavigation,
        ...navigationItems,
      ];
      if (user.is_active !== false) {
        items.push({ name: t('navigation.invitations'), href: '/invitations', icon: EnvelopeIcon });
      }
      items.push(
        { name: t('navigation.logs'), href: '/logs', icon: ClipboardDocumentListIcon },
        { name: t('navigation.devices'), href: '/devices', icon: ComputerDesktopIcon },
        { name: t('navigation.contacts'), href: '/contacts', icon: UserGroupIcon },
      );
      return items;
    }

    return baseNavigation;
  };

  // Get administration submenu items based on effective mode
  const getAdministrationItems = () => {
    if (!isAdmin) return [];
    const items = [
      { name: t('navigation.dashboard'), href: '/', icon: HomeIcon },
      { name: t('navigation.assets'), href: '/assets', icon: DocumentIcon },
      { name: t('navigation.folders'), href: '/folders', icon: FolderIcon },
      { name: t('navigation.users'), href: '/users', icon: UsersIcon },
      { name: t('navigation.devices'), href: '/devices', icon: ComputerDesktopIcon },
      { name: t('navigation.emails'), href: '/email-templates', icon: DocumentTextIcon },
    ];
    
    // Admin mode items (visible to both company admins and sysadmins in admin mode only)
    if (effectiveMode === 'admin') {
      // Watermarks, Policy, Passwords, Notifications, Asset Protection - visible only in admin mode. Invitations only for active users (suspended org admins are inactive).
      items.push({ name: t('navigation.watermarks'), href: '/administration/watermarks', icon: RectangleStackIcon });
      items.push({ name: t('navigation.policy'), href: '/administration/policy', icon: ShieldCheckIcon });
      items.push({ name: t('navigation.passwords'), href: '/administration/passwords', icon: KeyIcon });
      items.push({ name: t('navigation.notifications'), href: '/administration/notifications', icon: BellIcon });
      // License alerts: only in sidebar for sysadmins; company admins can still open /administration/licenses?company_id=... via direct link (e.g. from low-license or expiry notifications)
      if (isSysadmin) {
        items.push({ name: t('navigation.licenses'), href: '/administration/licenses', icon: ExclamationTriangleIcon });
      }
      if (user.is_active !== false) {
        items.push({ name: t('navigation.invitations'), href: '/invitations', icon: EnvelopeIcon });
      }
      items.push({ name: t('navigation.usages'), href: '/administration/usages', icon: ChartBarIcon });
      items.push({ name: t('navigation.assetProtection'), href: '/administration/asset-protection', icon: LockClosedIcon });
      items.push({ name: 'Logs', href: '/logs', icon: ClipboardDocumentListIcon });
      // Resources: hidden for now; route and backend registration commented out. Re-enable when needed.
      // if (isSysadmin || companyResources?.allow_company_admins_resources) {
      //   items.push({ name: t('navigation.resources'), href: '/resources', icon: CircleStackIcon });
      // }
      
      // Organisations - sysadmin always; company admin only when their company manages other orgs (own org edited via Settings)
      if (isSysadmin || hasManagedCompanies) {
        items.push({ name: t('navigation.clients'), href: '/clients', icon: BuildingOfficeIcon });
      }
    }
    
    // SysAdmin mode only items
    if (effectiveMode === 'sysadmin' && isSysadmin) {
      // items.push({ name: t('navigation.resources'), href: '/resources', icon: CircleStackIcon }); // hidden for now
      // Organisations - visible in sysadmin mode
      items.push({ name: t('navigation.clients'), href: '/clients', icon: BuildingOfficeIcon });
      // Invitations (select company to view/create) - same content as admin view but in sysadmin sidebar
      if (user.is_active !== false) {
        items.push({ name: t('navigation.invitations'), href: '/invitations', icon: EnvelopeIcon });
      }
      // Subscriptions & monitoring
      items.push({ name: t('navigation.subscriptions'), href: '/administration/subscriptions', icon: ChartBarIcon });
      items.push({ name: t('navigation.platformDefaults'), href: '/administration/platform-defaults', icon: AdjustmentsHorizontalIcon });
      // Notifications - visible in sysadmin mode for license alert settings
      items.push({ name: t('navigation.notifications'), href: '/administration/notifications', icon: BellIcon });
      items.push({ name: t('navigation.licenses'), href: '/administration/licenses', icon: ExclamationTriangleIcon });
      items.push({ name: t('navigation.usages'), href: '/administration/usages', icon: ChartBarIcon });
      // Translations - only in sysadmin mode
      items.push({ name: t('navigation.translations'), href: '/translations', icon: LanguageIcon });
      items.push({ name: 'Logs', href: '/administration/logs', icon: ClipboardDocumentListIcon });
    }
    
    return items;
  };

  const navigation = getNavigation();
  const administrationItems = getAdministrationItems();

  // Auditors: only Logs in sidebar; Account via header profile menu (/profile). No search, downloads, settings.
  const bottomNavigation = isAuditor
    ? []
    : isReader
      ? [
          // Readers don't get Settings — the page exposes Quick-Encrypt
          // policy defaults, watermark, notification, and security
          // preferences that only make sense for full-mode users.
          // Account info stays reachable via the header user-menu.
          { name: t('navigation.downloads'), href: '/downloads', icon: ArrowDownTrayIcon },
        ]
      : [
          { name: t('navigation.support'), href: '/support', icon: QuestionMarkCircleIcon },
          { name: t('navigation.downloads'), href: '/downloads', icon: ArrowDownTrayIcon },
          { name: t('navigation.settings'), href: '/settings', icon: Cog6ToothIcon },
        ];

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    if (href === '/clients') {
      return location.pathname === '/clients' || location.pathname.startsWith('/clients/');
    }
    return location.pathname === href;
  };

  const handleNavigation = (href: string, name: string) => {
    console.log('🔍 SIDEBAR DEBUG: Navigation clicked');
    console.log('  - href:', href);
    console.log('  - name:', name);
    console.log('  - current location:', location.pathname);
    console.log('  - will navigate to:', href);

    // Force navigation by updating window location (bypasses React Router blocks)
    if (href !== location.pathname) {
      console.log('  - forcing navigation to:', href);
      window.location.href = href;
    } else {
      console.log('  - already on this page, no navigation needed');
    }
  };

  // Persist sidebar state to localStorage and dispatch event
  useEffect(() => {
    localStorage.setItem('sidebar_collapsed', JSON.stringify(isManuallyCollapsed));
    // Dispatch custom event so Header can update
    window.dispatchEvent(new CustomEvent('sidebar-toggle'));
  }, [isManuallyCollapsed]);

  // Persist view mode to localStorage and dispatch event
  useEffect(() => {
    localStorage.setItem('view_mode', viewMode);
    // Calculate effectiveMode fresh to ensure we have the latest value
    const currentEffectiveMode = isSysadmin ? viewMode : (user?.role === 'admin' ? (viewMode === 'sysadmin' ? 'admin' : viewMode) : 'user');
    
    // Dispatch custom event so pages can react to mode changes
    window.dispatchEvent(new CustomEvent('view-mode-changed', { 
      detail: { viewMode, effectiveMode: currentEffectiveMode } 
    }));
    // Also dispatch legacy event for backward compatibility during transition
    window.dispatchEvent(new CustomEvent('administration-view-changed', { 
      detail: { isAdministrationView: currentEffectiveMode === 'admin' || currentEffectiveMode === 'sysadmin' } 
    }));
  }, [viewMode, isSysadmin, user?.role]);

  // Update window width on resize and reset manual override when crossing threshold
  React.useEffect(() => {
    const handleResize = () => {
      const newWidth = window.innerWidth;
      const wasAboveThreshold = windowWidth >= 1280;
      const isAboveThreshold = newWidth >= 1280;

      // Reset manual override when crossing the threshold
      if (wasAboveThreshold !== isAboveThreshold) {
        setIsManuallyCollapsed(null);
      }

      setWindowWidth(newWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [windowWidth]);

  // Determine if sidebar should be collapsed (manual override or responsive)
  const isCollapsed = isManuallyCollapsed !== null ? isManuallyCollapsed : windowWidth < 1280;

  // Expose sidebar width for fixed elements (e.g. sticky download bar) so they start after sidebar
  useEffect(() => {
    const width = isCollapsed ? '4rem' : '12rem';
    document.documentElement.style.setProperty('--sidebar-width', width);
    return () => {
      document.documentElement.style.removeProperty('--sidebar-width');
    };
  }, [isCollapsed]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartX.current = e.clientX;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;

    const deltaX = e.clientX - dragStartX.current;
    const threshold = 50; // Minimum drag distance to trigger toggle

    if (Math.abs(deltaX) > threshold) {
      if (deltaX < 0) {
        // Dragging left - collapse
        setIsManuallyCollapsed(true);
      } else {
        // Dragging right - expand
        setIsManuallyCollapsed(false);
      }
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, [isDragging]);

  const handleMouseUp = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
  }, [isDragging]);

  // Add global mouse event listeners
  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div
      ref={sidebarRef}
      className={`flex flex-col bg-black text-white flex-shrink-0 h-screen transition-all duration-300 relative ${isCollapsed ? 'w-16' : 'w-48'} ${isOnboardingOpen ? 'z-[101]' : ''}`}
    >
      {/* Logo and Brand */}
      <div className={`flex items-center ${isCollapsed ? 'justify-center pt-2 pb-1' : 'px-3 pt-2 pb-1'}`}>
        <Link to="/" className="cursor-pointer">
          <img
            src="/favicon.png"
            alt="Governate Logo"
            className={`w-8 h-8 hover:opacity-80 transition-opacity ${isCollapsed ? 'block' : 'hidden'}`}
          />
          <img
            src="/logo-gov.svg"
            alt="Governate Logo"
            className={`w-32 h-12 hover:opacity-80 transition-opacity ${isCollapsed ? 'hidden' : 'block'}`}
          />
        </Link>
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 px-3 pt-2 space-y-[0.175rem] overflow-y-auto">
        {/* Show main navigation when in user mode (or reader mode with reduced nav) */}
        {(effectiveMode === 'user' || isReader || isAuditor) && navigation.map((item) => {
          const Icon = item.icon;
          const isNotificationsItem = item.name === 'Notifications';
          const showUnreadDot = isNotificationsItem && unreadCount > 0;
          return (
            <div
              key={item.name}
              onClick={() => handleNavigation(item.href, item.name)}
              className={`sidebar-item cursor-pointer relative ${isActive(item.href)
                ? 'sidebar-item-active'
                : 'sidebar-item-inactive'
                } ${isOnboardingStepHighlight(item.href) ? 'z-[101] relative border-l-4 border-green-500 bg-green-500/25' : ''}`}
              title=""
            >
              <div className="relative flex items-center">
                <div className="relative">
                  <Icon className={`w-5 h-5 ${isCollapsed ? 'mr-0' : 'mr-3'}`} />
                  {showUnreadDot && (
                    <div className="absolute -top-0.5 -left-0.2 bg-red-500 rounded-full h-2 w-2 border border-black"></div>
                  )}
                </div>
              </div>
              <span className={isCollapsed ? 'hidden' : 'inline'}>{item.name}</span>
            </div>
          );
        })}

        {/* Show administration items when in admin or sysadmin mode (never for reader) */}
        {(effectiveMode === 'admin' || effectiveMode === 'sysadmin') && isAdmin && !isReader && administrationItems.length > 0 && (
          <div className="space-y-[0.175rem]">
            {administrationItems.map((item) => {
              const Icon = item.icon;
              // Only show notification dot in user/standard mode, not in admin or sysadmin mode
              const showUnreadDot = false; // Never show in admin/sysadmin mode
              const itemIsActive = isActive(item.href);
              return (
                <div
                  key={item.name}
                  onClick={() => handleNavigation(item.href, item.name)}
                  className={`sidebar-item cursor-pointer relative ${itemIsActive
                    ? 'sidebar-item-active'
                    : 'sidebar-item-inactive'
                    } ${isOnboardingStepHighlight(item.href) ? 'z-[101] relative border-l-4 border-green-500 bg-green-500/25' : ''} ${isCollapsed ? 'justify-center' : ''}`}
                  title={isCollapsed ? item.name : ''}
                >
                  <div className="relative flex items-center">
                    <div className="relative">
                      <Icon className={`w-5 h-5 ${isCollapsed ? 'mr-0' : 'mr-3'}`} />
                      {showUnreadDot && (
                        <div className="absolute -top-0.5 -left-0.2 bg-red-500 rounded-full h-2 w-2 border border-black"></div>
                      )}
                    </div>
                  </div>
                  <span className={isCollapsed ? 'hidden' : 'inline'}>{item.name}</span>
                </div>
              );
            })}
          </div>
        )}
      </nav>

      {/* Bottom Navigation */}
      <div className={`px-3 ${isCollapsed ? 'py-4' : 'py-3'} border-t border-gray-600`}>
        {/* Global Search Trigger - hidden for auditors (they only have Logs + Account) */}
        {!isAuditor && (
          <div
            onClick={() => setIsSearchOpen(true)}
            className={`sidebar-item cursor-pointer mb-2 ${isCollapsed ? 'justify-center' : ''
              } text-gray-300 hover:bg-gray-800 hover:text-white`}
            title="Global Search"
          >
            <div className="relative flex items-center">
              <MagnifyingGlassIcon className={`w-5 h-5 ${isCollapsed ? 'mr-0' : 'mr-3'}`} />
              <span className={isCollapsed ? 'hidden' : 'inline'}>{t('navigation.search')}</span>
            </div>
          </div>
        )}

        <div className="space-y-[0.175rem]">
          {bottomNavigation.map((item) => {
            const Icon = item.icon;
            const isDownloads = item.href === '/downloads';
            const showDot = isDownloads && showDownloadsIndicator;
            return (
              <div
                key={item.name}
                onClick={() => handleNavigation(item.href, item.name)}
                className={`sidebar-item cursor-pointer ${isActive(item.href)
                  ? 'sidebar-item-active'
                  : 'sidebar-item-inactive'
                  } ${isOnboardingStepHighlight(item.href) ? 'z-[101] relative border-l-4 border-green-500 bg-green-500/25' : ''}`}
                title=""
              >
                <Icon className={`w-5 h-5 flex-shrink-0 ${isCollapsed ? 'mr-0' : 'mr-3'}`} />
                <span className={isCollapsed ? 'hidden' : 'inline'}>{item.name}</span>
                {showDot && (
                  <span className={`flex-shrink-0 bg-green-500 rounded-full h-2 w-2 border border-black ${isCollapsed ? 'ml-0' : 'ml-1.5'}`} aria-hidden />
                )}
              </div>
            );
          })}
        </div>

        {/* Mode Selector - Only for admins (not translators, not reader). Highlighted when onboarding step is mode switch. */}
        {isAdmin && !isTranslator && !isReader && (
          <div
            className={`${isCollapsed ? 'mt-4 pt-4' : 'mt-1.5 pt-1.5 pb-1'} border-t border-gray-600 ${isCollapsed ? 'flex justify-center' : ''} ${isOnboardingModeSwitchStep ? 'z-[101] rounded-lg ring-2 ring-green-500 bg-green-500/25 p-2 -mx-1' : ''}`}
          >
            <ModeSelector
              mode={viewMode}
              onChange={setViewMode}
              isSysadmin={isSysadmin}
              collapsed={isCollapsed}
            />
          </div>
        )}

        {/* User Profile */}

      </div>

      {/* Resize Handle */}
      <div
        className={`absolute top-0 right-0 w-1 h-full bg-gray-600 hover:bg-gray-500 cursor-col-resize transition-colors ${isDragging ? 'bg-blue-500' : ''
          }`}
        onMouseDown={handleMouseDown}
        title="Drag to resize sidebar"
      />

      {/* Global Search Modal */}
      <GlobalSearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </div>
  );
};

export default Sidebar;
